import React, { useEffect, useState } from "react";
import { Grid, Typography, Box } from "@mui/material";
import ProfileSection from "../ProfileSection";
import TotalTransactions from "../TotalTransactions";
import Users from "./icons/Users";
import TransactionChart from "./TransactionChart";
import RevenueChart from "./RevenueChart";
import { useQuery, useSubscription } from "@apollo/client";
import { API_TOTAL_REVENUE_QUERY } from "../../graphql/query";
import UnitsConsumed from "../UnitsConsumed";
import TotalRevenue from "../TotalRevenue";
import TotalProfit from "../TotalProfit";
import FacilityFilterBtn from "../FacilityFilter";
import { useSelector } from "react-redux";
import ChargeUsageStatue from "./ChargeUsageStatue";
import UnitsConsumedChart from "./UnitsConsumedChart";
import TotalCompanyProfit from "../TotalCompanyProfit";
import { useTranslation } from "react-i18next";
import NewDatePicker from "../NewDatePicker";
import ChargerUsageChart from "../ChargerUsageChart";
import ElectricityConsumed from "../ElectricityConsumed";
import CircularProgress from "@mui/material/CircularProgress";
import PowerTypeFilter from "../PowerTypeFilter";
import FacilityTypeFilter from "../FacilityTypeFilter";
import { generateTotalRevenue } from "./libs/generateTotalRevenue";
import FacilityLocationTypeFilter from "../FacilityLocationTypeFilter";

const Overview = () => {
  const [totalUsers, setTotalUsers] = useState(0);
  const { selectedDate } = useSelector((state) => state.datePicker);
  const { t } = useTranslation();
  const [pageLoading, setPageLoading] = useState(true);
  const stationlist = useSelector((state) => state.stationList.data.data);
  const { selectedFacility } = useSelector((state) => state.stationList);
  const { appliedFilter } = useSelector((state) => state.powerTypeFilter);
  const appliedFacilityLocationTypeFilter = useSelector((state)=> state.facilityLocationTypeFilter.appliedFilter);

  const userData = JSON.parse(localStorage.getItem("userData"));
  const [userLoading, setUserLoading] = useState(false);

  const [stats, setStats] = useState(null);

  const { data, refetch } = useQuery(API_TOTAL_REVENUE_QUERY, {
    variables: {
      where: {
        facility_id: {
          _in:
            typeof selectedFacility?.id !== "number"
              ? selectedFacility?.id
              : [selectedFacility?.id],
        },
        created_at: {
          _gte: selectedDate?.start?.toISOString(),
          _lt: selectedDate?.end?.toISOString(),
        },
        ...(appliedFilter &&
          appliedFilter !== "Both" && {
            power_type: {
              _eq: appliedFilter,
            },
          }),
        ...(appliedFacilityLocationTypeFilter?.length > 0 &&
          appliedFacilityLocationTypeFilter[0] !== "all" && 
          selectedFacility?.name === "All" && {
            facility_type: {
              _eq: appliedFacilityLocationTypeFilter[0],
            },
          }),
      },
    },
  });

  const greetingMessage = () => {
    const hour = new Date().getHours();
    if (hour > 0 && hour < 12) return "Good Morning";
    else if (hour >= 12 && hour < 18) return "Good Afternoon";
    else return "Good Evening";
  };
  const customizeName = (name) => {
    let newName = name.split(" ").at(0);
    newName = newName.charAt(0).toUpperCase() + newName.substring(1);
    return newName;
  };

  useEffect(() => {
    if (selectedFacility.id.length || selectedFacility.id > 0)
      setPageLoading(false);
  }, [selectedFacility]);

  useEffect(() => {
    if (data && data.view_facility_revenue_aggregate) {
      (async () => {
        let uniqueCount = 0;
        const counts = [];

        for (const item of data.view_facility_revenue_aggregate.nodes) {
          if (!counts[item.user_id]) {
            counts[item.user_id] = true; // Mark as seen
            uniqueCount++;
          }
        }

        setTotalUsers(uniqueCount);
      })();

      setUserLoading(false);
      setStats(
        generateTotalRevenue(
          data.view_facility_revenue_aggregate.nodes,
          stationlist
        )
      );
    }
  }, [data]);

  useEffect(() => {
    const overViewInterval = setInterval(() => {
      refetch();
    }, 10000);

    return () => {
      clearInterval(overViewInterval);
    };
  }, []);

  return (
    <>
      <Grid
        container
        rowSpacing={3}
        columnSpacing={3}
        alignItems="center"
        style={{ backgroundColor: "white", marginBottom: "20px" }}
      >
        <Grid
          item
          xs={8}
          style={{
            flexGrow: 1,
            padding: "0 20px",
            alignItems: "center",
            display: "flex",
          }}
        >
          <Typography variant="h4" fontSize="24px" fontWeight="600">
            {greetingMessage()},
            <Typography variant="subtitle" color="#467CEB">
              {" "}
              {customizeName(userData?.data?.name ?? "")} !
            </Typography>
          </Typography>
        </Grid>
        <Grid item xs={4} style={{ paddingTop: "5px" }}>
          <ProfileSection />
        </Grid>
      </Grid>
      {pageLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "70vh",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container mb="20px" columnSpacing={2} alignItems="center">
            <Grid item>
              <NewDatePicker />
            </Grid>
            <Grid item>
              <FacilityFilterBtn />
            </Grid>
            <Grid item>
              <PowerTypeFilter />
            </Grid>
            <Grid item>
              <FacilityTypeFilter />
            </Grid>
            <Grid item>
              <FacilityLocationTypeFilter />
            </Grid>
          </Grid>
          <Grid
            container
            spacing={3}
            columnSpacing={3}
            sx={{ marginBottom: 2 }}
          >
            <Grid item xs={3}>
              <Grid
                container
                justifyContent="center"
                sx={{ flexGrow: 1 }}
                alignItems="center"
                rowSpacing={3}
              >
                <Grid item xs={12}>
                  <TotalRevenue stats={stats} />
                </Grid>
                <Grid item xs={12}>
                  <ElectricityConsumed stats={stats} />
                </Grid>
                <Grid item xs={12}>
                  <TotalCompanyProfit stats={stats} />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={9} style={{ flexGrow: 1 }}>
              <UnitsConsumedChart data={data} />
            </Grid>
          </Grid>
          <Grid container justifyContent="center" columnSpacing={3}>
            <Grid item xs={12}>
              <Grid
                container
                alignItems="center"
                rowSpacing={3}
                columnSpacing={3}
                sx={{ marginBottom: 2 }}
              >
                <Grid item xs={3}>
                  <TotalProfit stats={stats} />
                </Grid>
                <Grid item xs={3}>
                  <UnitsConsumed data={data} />
                </Grid>
                <Grid item xs={3}>
                  <TotalTransactions data={data} />
                </Grid>
                <Grid item xs={3}>
                  <Box
                    display={"flex"}
                    alignItems={"center"}
                    bgcolor={"white"}
                    justifyContent={"space-between"}
                    borderRadius={"16px"}
                    padding={"24px"}
                    height="130px"
                  >
                    <Typography>
                      <Typography
                        variant="subtitle"
                        fontSize="12px"
                        fontWeight="600"
                        style={{ textWrap: "nowrap" }}
                      >
                        {t("totalUsers")}
                        {userLoading ? (
                          <span style={{ color: "#467CEB" }}>
                            {" "}
                            {t("loading")}...
                          </span>
                        ) : (
                          ""
                        )}
                      </Typography>
                      <Typography
                        variant="h5"
                        fontSize="24px"
                        fontWeight="700"
                        sx={{ marginTop: "10px" }}
                        style={{ textWrap: "nowrap" }}
                      >
                        {totalUsers}
                      </Typography>
                    </Typography>
                    <Users />
                  </Box>
                </Grid>
              </Grid>

              <Grid
                container
                justifyContent="start"
                sx={{ flexGrow: 1 }}
                alignItems="center"
                rowSpacing={3}
                columnSpacing={3}
              >
                <Grid item xs={8.5}>
                  <TransactionChart data={data} height="411" />
                </Grid>
                <Grid item md={3.5}>
                  <ChargeUsageStatue
                    facilityFilterID={selectedFacility.id}
                    facilityFilterName={selectedFacility.name}
                    powerTypeFilter={appliedFilter}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid container xs={12} pt={2}>
            <Grid item xs={12}>
              <RevenueChart data={data} height={226} />
            </Grid>
            {selectedFacility?.name !== "All" && (
              <Grid item xs={12} pt={2}>
                <ChargerUsageChart data={data} />
              </Grid>
            )}
          </Grid>
        </>
      )}
    </>
  );
};
export default Overview;
